import { StatusPill } from '@/design-system';
import type { PublicDocument, PublicLanguage } from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { formatDate, formatDateTime } from '../format';
import type { PublicDocumentState } from '../usePublicReport';
import { DocumentLink, DocumentUnavailable } from './DocumentLink';

export interface ReportHeroProps {
    service: string;
    address: string;
    summary: string | null;
    publishedAt: string | null;
    /** Tot wanneer deze link werkt. */
    validUntil: string | null;
    documents: PublicDocument[];
    language: PublicLanguage;
    copy: PublicCopy;
    headingLevel: 1 | 2;
    /**
     * De knop haalt de PDF zelf op: met de sessie in de kantoorpreview, met de
     * link op de publieke pagina. `false`: de browser volgt de link.
     */
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    documentState?: PublicDocumentState;
}

/**
 * De kop van de pagina: status, dienst en adres, één regel samenvatting en de
 * downloadknoppen van 48 px. De eerste is de enige primaire actie; daaronder
 * staat tot wanneer de link werkt.
 */
export function ReportHero({
    service,
    address,
    summary,
    publishedAt,
    validUntil,
    documents,
    language,
    copy,
    headingLevel,
    onOpenDocument,
    documentState,
}: ReportHeroProps) {
    const Heading = headingLevel === 1 ? 'h1' : 'h2';
    const moment = formatDateTime(publishedAt, language);
    const until = formatDate(validUntil, language);

    return (
        <section className="border-b border-[var(--border-subtle)] px-[var(--pr-pad-x)] pt-7 pb-[26px]">
            <StatusPill tone="accent" size="lg" weight="medium" bordered dot className="mb-[14px]">
                {moment ? `${copy.reportReady} · ${moment}` : copy.reportReady}
            </StatusPill>

            <Heading className="m-0 font-[family-name:var(--font-display)] text-[clamp(24px,4.4cqi,30px)] leading-[1.1] font-semibold tracking-[-0.025em] text-pretty text-[var(--text-primary)]">
                {service}
                <br />
                {address}
            </Heading>

            {summary ? (
                <p className="mt-[10px] mb-0 text-[14px] leading-[1.55] text-[var(--text-tertiary-safe)]">
                    {summary}
                </p>
            ) : null}

            {documents.length > 0 ? (
                <div className="mt-[18px] flex flex-wrap gap-[9px]">
                    {documents.map((document, index) => (
                        <DocumentLink
                            key={document.key}
                            document={document}
                            onOpenDocument={onOpenDocument}
                            busy={documentState?.busy === document.key}
                            size="xl"
                            variant={index === 0 ? 'primary' : 'outline'}
                            copy={copy}
                        />
                    ))}
                </div>
            ) : null}

            {documents.some((document) => document.key === documentState?.failed) ? (
                <DocumentUnavailable copy={copy} />
            ) : null}

            {until ? (
                <p className="mt-3 mb-0 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                    {copy.validUntil(until)}
                </p>
            ) : null}
        </section>
    );
}
